import React, { useEffect, useState } from 'react';
import {
  Form,
  Input,
  Button,
  Card,
  Row,
  Col,
  Select,
  Upload,
  Space,
  App,
  Typography,
  Spin,
} from 'antd';
import {
  PlusOutlined,
  UploadOutlined,
  MinusCircleOutlined,
  ArrowLeftOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';

const { Title } = Typography;
const { TextArea } = Input;

// Helper to manage file uploads for Ant Design Form
const normFile = (e: any) => {
  if (Array.isArray(e)) {
    return e;
  }
  return e && e.fileList;
};

const PropertyAreaForm: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const isEditMode = !!id;
  const { message } = App.useApp();

  // Helper function to upload a single file
  const uploadFile = async (file: any) => {
    if (!file || !file.originFileObj) {
      // This can happen if the file is already uploaded (has a URL but no originFileObj)
      if (file.url) {
        return file.url;
      }
      throw new Error('File object is invalid for upload.');
    }
    const formData = new FormData();
    formData.append('file', file.originFileObj); // Ensure backend expects the key 'file'
    try {
      const response = await api.post('/upload', formData);
      return response.data.data.url; // Assuming the URL is in response.data.data.url
    } catch (error) {
      message.error(`Gagal mengupload file: ${file.name}`);
      throw error; // Re-throw to stop the submission process
    }
  };

  // --- Validation Functions ---
  const validateImage = (file: File) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      message.error('Format file yang diizinkan hanya JPG, JPEG, PNG');
    }
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('Ukuran file harus lebih kecil dari 10MB!');
    }
    // Return false to prevent automatic upload but allow file to be added to the list if valid
    return isJpgOrPng && isLt10M ? false : Upload.LIST_IGNORE;
  };

  const validatePdf = (file: File) => {
    const isPdf = file.type === 'application/pdf';
    if (!isPdf) {
      message.error('Format file yang diizinkan hanya PDF!');
    }
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('Ukuran file harus lebih kecil dari 10MB!');
    }
    return isPdf && isLt10M ? false : Upload.LIST_IGNORE;
  };

  useEffect(() => {
    if (isEditMode) {
      const fetchPropertyArea = async () => {
        setPageLoading(true);
        try {
          const response = await api.get(`property-areas/${id}`);
          const data = response.data.data;

          // Format data for Ant Design's Upload component
          const formattedData = {
            ...data,
            header_logo: data.header_logo_url
              ? [{ uid: '-1', name: 'logo', status: 'done', url: data.header_logo_url }]
              : [],
            e_brochure: data.e_brochure_url
              ? [{ uid: '-2', name: 'e-brochure', status: 'done', url: data.e_brochure_url }]
              : [],
            price_list: data.price_list_url
              ? [{ uid: '-3', name: 'price-list', status: 'done', url: data.price_list_url }]
              : [],
            photos: (data.photos || []).map((p: any, index: number) => ({
              photo: [{
                uid: p.id || `-${index + 4}`,
                name: `photo_${index}`,
                status: 'done',
                url: p.image_url,
              }],
              caption: p.caption,
            })),
          };
          form.setFieldsValue(formattedData);
        } catch (error) {
          message.error('Gagal memuat data property area');
        } finally {
          setPageLoading(false);
        }
      };
      fetchPropertyArea();
    }
  }, [id, isEditMode, form, message]);

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      // --- STAGE 1: UPLOAD FILES AND GET URLs ---
      const uploadedUrls = {
        header_logo_url: values.header_logo?.[0]?.url, // Keep existing URL if not changed
        e_brochure_url: values.e_brochure?.[0]?.url,
        price_list_url: values.price_list?.[0]?.url,
      };

      // Upload single files if they are new
      if (values.header_logo && values.header_logo[0]?.originFileObj) {
        uploadedUrls.header_logo_url = await uploadFile(values.header_logo[0]);
      }
      if (values.e_brochure && values.e_brochure[0]?.originFileObj) {
        uploadedUrls.e_brochure_url = await uploadFile(values.e_brochure[0]);
      }
      if (values.price_list && values.price_list[0]?.originFileObj) {
        uploadedUrls.price_list_url = await uploadFile(values.price_list[0]);
      }

      // Upload multiple photos in parallel
      let uploadedPhotos = [];
      if (values.photos && values.photos.length > 0) {
        const photoPromises = values.photos.map(async (photoItem: any) => {
          const file = photoItem.photo?.[0];
          if (!file) return null;

          let imageUrl = file.url; // Keep existing URL by default
          if (file.originFileObj) {
            // If there's a new file object, upload it
            imageUrl = await uploadFile(file);
          }

          if (imageUrl) {
            return { image_url: imageUrl, caption: photoItem.caption || '', display_order: 0 };
          }
          return null;
        });
        uploadedPhotos = (await Promise.all(photoPromises)).filter(p => p !== null);
      }

      // --- STAGE 2: PREPARE AND SUBMIT TEXT DATA WITH URLs ---
      const payload = {
        name: values.name,
        address: values.address,
        phone_number: values.phone_number,
        description: values.description,
        is_active: true, // Assuming default is active
        promotion_labels: JSON.stringify(values.promotion_labels || []),
        project_info: JSON.stringify(values.project_info || []),
        header_logo_url: uploadedUrls.header_logo_url || '',
        e_brochure_url: uploadedUrls.e_brochure_url || '',
        price_list_url: uploadedUrls.price_list_url || '',
        photos: uploadedPhotos,
      };

      if (isEditMode) {
        await api.put(`property-areas/${id}`, payload);
        message.success('Property Area berhasil diperbarui');
      } else {
        await api.post('property-areas', payload);
        message.success('Property Area berhasil ditambahkan');
      }
      navigate('/property/areas');
    } catch (error: any) {
      // Error is already messaged in the upload helper, but we add a generic one here too.
      console.error('Submission failed:', error);
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) {
    return <Spin size="large" style={{ display: 'block', marginTop: '50px' }} />;
  }

  return (
    <>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            {isEditMode ? 'Edit Property Area' : 'Tambah Property Area'}
          </Title>
        </Col>
        <Col>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/property/areas')}>
            Kembali
          </Button>
        </Col>
      </Row>
      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{ project_info: [{ key: '', value: '' }] }}
        >
          <Form.Item name="name" label="Nama Property Area" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Form.Item name="address" label="Alamat" rules={[{ required: true }]}>
            <TextArea rows={3} />
          </Form.Item>

          <Form.Item
            name="phone_number"
            label="Nomor Telepon"
            rules={[{ required: true, pattern: /^\d+$/, message: 'Hanya angka yang diperbolehkan' }]}
          >
            <Input type="number" />
          </Form.Item>

          <Form.Item name="description" label="Deskripsi">
            <Input />
          </Form.Item>

          <Form.Item name="promotion_labels" label="Label Promosi">
            <Select mode="tags" style={{ width: '100%' }} placeholder="Conto: Diskon DP 10%" />
          </Form.Item>

          <Form.Item label="Info Proyek">
            <Form.List name="project_info">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                      <Form.Item
                        {...restField}
                        name={[name, 'key']}
                        rules={[{ required: true, message: 'Key harus diisi' }]}
                      >
                        <Input placeholder="Key (Contoh: Tipe Unit)" />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'value']}
                        rules={[{ required: true, message: 'Value harus diisi' }]}
                      >
                        <Input placeholder="Value (Contoh: Cluster)" />
                      </Form.Item>
                      <MinusCircleOutlined onClick={() => remove(name)} />
                    </Space>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      Tambah Info Proyek
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="header_logo"
                label="Header Logo"
                valuePropName="fileList"
                getValueFromEvent={normFile}
                extra="Format file JPG, JPEG, PNG. Max: 10MB."
              >
                <Upload listType="picture" maxCount={1} beforeUpload={validateImage} accept=".jpg,.jpeg,.png">
                  <Button icon={<UploadOutlined />}>Pilih Gambar</Button>
                </Upload>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="e_brochure"
                label="E-Brochure"
                valuePropName="fileList"
                getValueFromEvent={normFile}
                extra="Format file PDF. Max: 10MB."
              >
                <Upload maxCount={1} beforeUpload={validatePdf} accept=".pdf">
                  <Button icon={<UploadOutlined />}>Pilih PDF</Button>
                </Upload>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="price_list"
                label="Price List"
                valuePropName="fileList"
                getValueFromEvent={normFile}
                extra="Format file PDF. Max: 10MB."
              >
                <Upload maxCount={1} beforeUpload={validatePdf} accept=".pdf">
                  <Button icon={<UploadOutlined />}>Pilih PDF</Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Foto-foto Property Area">
            <Form.List name="photos">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Row key={key} gutter={16} align="top">
                      <Col span={12}>
                        <Form.Item
                          {...restField}
                          name={[name, 'photo']}
                          valuePropName="fileList"
                          getValueFromEvent={normFile}
                          rules={[{ required: true, message: 'File harus di-upload' }]}
                          extra="Format file JPG, JPEG, PNG. Max: 10MB."
                        >
                          <Upload
                            listType="picture"
                            maxCount={1}
                            beforeUpload={validateImage}
                            accept=".jpg,.jpeg,.png"
                          >
                            <Button icon={<UploadOutlined />}>Pilih Gambar</Button>
                          </Upload>
                        </Form.Item>
                      </Col>
                      <Col span={10}>
                        <Form.Item {...restField} name={[name, 'caption']}>
                          <Input placeholder="Caption untuk foto ini" />
                        </Form.Item>
                      </Col>
                      <Col span={2}>
                        <MinusCircleOutlined onClick={() => remove(name)} style={{ marginTop: '8px' }} />
                      </Col>
                    </Row>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      Tambah Foto
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Form.Item>

          <Form.Item style={{ marginTop: 32 }}>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />} size="large">
                {isEditMode ? 'Update Property Area' : 'Simpan Property Area'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </>
  );
};

export default PropertyAreaForm;